import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  User, Mail, Phone, MapPin, Briefcase, GraduationCap, Award,
  BookOpen, ArrowRight, ArrowLeft, Check, Loader2, Plus, Trash2,
  Globe, Linkedin, Video
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available subjects for mentors
const MENTOR_SUBJECTS = [
  "Design Foundation",
  "Illustrator",
  "Photoshop",
  "Blender",
  "Animation",
  "Modelling",
  "Texture",
  "Shading",
  "Rigging",
  "Lighting",
  "Compositing",
  "Editing",
  "DI",
  "Visual Effects",
  "Roto",
  "Paint",
  "Clean Up",
  "Production Management",
  "Story Boarding"
];

const LANGUAGES = ["English", "Hindi", "Tamil", "Telugu", "Kannada", "Malayalam", "Marathi", "Bengali", "Gujarati"];

export default function MentorRegistration() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Info
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    
    // Professional Info
    professional_title: '',
    years_of_experience: 0,
    current_organization: '',
    portfolio_url: '',
    linkedin_url: '',
    demo_reel_url: '',
    
    // Academics
    academics: [{ degree: '', institution: '', year: new Date().getFullYear(), specialization: '' }],
    
    // Work Experience
    work_experience: [{ company: '', role: '', start_year: 2020, end_year: null, description: '' }],
    
    // Accolades
    accolades: [],
    
    // Teaching
    subjects: [],
    teaching_experience_years: 0,
    preferred_languages: ['English'],
    availability_hours_per_week: 10,
    bio: ''
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addAcademic = () => {
    setFormData(prev => ({
      ...prev,
      academics: [...prev.academics, { degree: '', institution: '', year: new Date().getFullYear(), specialization: '' }]
    }));
  };

  const removeAcademic = (index) => {
    setFormData(prev => ({
      ...prev,
      academics: prev.academics.filter((_, i) => i !== index)
    }));
  };

  const updateAcademic = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      academics: prev.academics.map((a, i) => i === index ? { ...a, [field]: value } : a)
    }));
  };

  const addWorkExperience = () => {
    setFormData(prev => ({
      ...prev,
      work_experience: [...prev.work_experience, { company: '', role: '', start_year: 2020, end_year: null, description: '' }]
    }));
  };

  const removeWorkExperience = (index) => {
    setFormData(prev => ({
      ...prev,
      work_experience: prev.work_experience.filter((_, i) => i !== index)
    }));
  };

  const updateWorkExperience = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      work_experience: prev.work_experience.map((w, i) => i === index ? { ...w, [field]: value } : w)
    }));
  };

  const addAccolade = () => {
    setFormData(prev => ({
      ...prev,
      accolades: [...prev.accolades, { title: '', organization: '', year: new Date().getFullYear(), description: '' }]
    }));
  };

  const removeAccolade = (index) => {
    setFormData(prev => ({
      ...prev,
      accolades: prev.accolades.filter((_, i) => i !== index)
    }));
  };

  const updateAccolade = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      accolades: prev.accolades.map((a, i) => i === index ? { ...a, [field]: value } : a)
    }));
  };

  const toggleSubject = (subject) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject]
    }));
  };

  const toggleLanguage = (lang) => {
    setFormData(prev => ({
      ...prev,
      preferred_languages: prev.preferred_languages.includes(lang)
        ? prev.preferred_languages.filter(l => l !== lang)
        : [...prev.preferred_languages, lang]
    }));
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        if (!formData.name || !formData.email || !formData.password || !formData.phone) {
          toast.error('Please fill all required personal information');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return false;
        }
        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          return false;
        }
        return true;
      case 2:
        if (!formData.professional_title || formData.years_of_experience < 0) {
          toast.error('Please fill professional information');
          return false;
        }
        return true;
      case 3:
        if (formData.academics.length === 0 || !formData.academics[0].degree) {
          toast.error('Please add at least one academic qualification');
          return false;
        }
        return true;
      case 4:
        if (formData.subjects.length === 0) {
          toast.error('Please select at least one subject to teach');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/mentor/register`, {
        ...formData,
        academics: formData.academics.filter(a => a.degree),
        work_experience: formData.work_experience.filter(w => w.company),
        accolades: formData.accolades.filter(a => a.title)
      }, { withCredentials: true });
      
      toast.success(`Registration successful! Your Vendor ID: ${response.data.vendor_id}`);
      setStep(5);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const cardClass = theme === 'dark' ? 'card-shadow-dark' : 'card-shadow-light';

  const steps = [
    { num: 1, title: 'Personal Info', icon: User },
    { num: 2, title: 'Professional', icon: Briefcase },
    { num: 3, title: 'Academics', icon: GraduationCap },
    { num: 4, title: 'Teaching', icon: BookOpen },
    { num: 5, title: 'Complete', icon: Check }
  ];

  return (
    <div className="min-h-screen pt-20 pb-12 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Badge className="mb-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
            Join Our Faculty
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            <span className="text-gradient-purple">Mentor Registration</span>
          </h1>
          <p className="text-muted-foreground">
            Share your expertise and inspire the next generation of artists
          </p>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex justify-between items-center mb-8 px-4">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all ${
                step >= s.num 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                {step > s.num ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className={`ml-2 text-sm font-medium hidden sm:block ${step >= s.num ? '' : 'text-muted-foreground'}`}>
                {s.title}
              </span>
              {i < steps.length - 1 && (
                <div className={`w-8 sm:w-16 h-1 mx-2 rounded ${step > s.num ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <Card className={`${cardClass}`}>
          <CardContent className="p-6 md:p-8">
            {/* Step 1: Personal Info */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      data-testid="mentor-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      data-testid="mentor-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Create password"
                      value={formData.password}
                      onChange={(e) => updateField('password', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm password"
                      value={formData.confirmPassword}
                      onChange={(e) => updateField('confirmPassword', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      data-testid="mentor-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dob">Date of Birth</Label>
                    <Input
                      id="dob"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => updateField('date_of_birth', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Gender</Label>
                    <Select value={formData.gender} onValueChange={(v) => updateField('gender', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode</Label>
                    <Input
                      id="pincode"
                      placeholder="400001"
                      value={formData.pincode}
                      onChange={(e) => updateField('pincode', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Textarea
                    id="address"
                    placeholder="Your address"
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      placeholder="Mumbai"
                      value={formData.city}
                      onChange={(e) => updateField('city', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      placeholder="Maharashtra"
                      value={formData.state}
                      onChange={(e) => updateField('state', e.target.value)}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Professional Info */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Professional Title *</Label>
                    <Input
                      id="title"
                      placeholder="e.g., Senior 3D Artist, VFX Supervisor"
                      value={formData.professional_title}
                      onChange={(e) => updateField('professional_title', e.target.value)}
                      data-testid="mentor-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="experience">Years of Experience *</Label>
                    <Input
                      id="experience"
                      type="number"
                      min="0"
                      value={formData.years_of_experience}
                      onChange={(e) => updateField('years_of_experience', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="org">Current Organization</Label>
                  <Input
                    id="org"
                    placeholder="Company/Studio name"
                    value={formData.current_organization}
                    onChange={(e) => updateField('current_organization', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="linkedin" className="flex items-center gap-2">
                    <Linkedin className="w-4 h-4" /> LinkedIn Profile
                  </Label>
                  <Input
                    id="linkedin"
                    placeholder="https://linkedin.com/in/yourprofile"
                    value={formData.linkedin_url}
                    onChange={(e) => updateField('linkedin_url', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reel" className="flex items-center gap-2">
                    <Video className="w-4 h-4" /> Demo Reel / Showreel URL
                  </Label>
                  <Input
                    id="reel"
                    placeholder="https://vimeo.com/yourreel or YouTube link"
                    value={formData.demo_reel_url}
                    onChange={(e) => updateField('demo_reel_url', e.target.value)}
                  />
                </div>

                {/* Work Experience */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-lg font-semibold">Work Experience</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addWorkExperience}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  
                  {formData.work_experience.map((exp, index) => (
                    <Card key={index} className="p-4 bg-muted/50">
                      <div className="flex justify-between items-start mb-3">
                        <span className="font-medium">Experience {index + 1}</span>
                        {index > 0 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeWorkExperience(index)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <Input
                          placeholder="Company/Studio"
                          value={exp.company}
                          onChange={(e) => updateWorkExperience(index, 'company', e.target.value)}
                        />
                        <Input
                          placeholder="Role/Designation"
                          value={exp.role}
                          onChange={(e) => updateWorkExperience(index, 'role', e.target.value)}
                        />
                        <Input
                          type="number"
                          placeholder="Start Year"
                          value={exp.start_year}
                          onChange={(e) => updateWorkExperience(index, 'start_year', parseInt(e.target.value))}
                        />
                        <Input
                          type="number"
                          placeholder="End Year (blank if current)"
                          value={exp.end_year || ''}
                          onChange={(e) => updateWorkExperience(index, 'end_year', e.target.value ? parseInt(e.target.value) : null)}
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 3: Academics & Accolades */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                {/* Academic Qualifications */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-lg font-semibold flex items-center gap-2">
                      <GraduationCap className="w-5 h-5" /> Academic Qualifications *
                    </Label>
                    <Button type="button" variant="outline" size="sm" onClick={addAcademic}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  
                  {formData.academics.map((academic, index) => (
                    <Card key={index} className="p-4 bg-muted/50">
                      <div className="flex justify-between items-start mb-3">
                        <span className="font-medium">Qualification {index + 1}</span>
                        {index > 0 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeAcademic(index)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <Input
                          placeholder="Degree (e.g., B.Des, MFA)"
                          value={academic.degree}
                          onChange={(e) => updateAcademic(index, 'degree', e.target.value)}
                        />
                        <Input
                          placeholder="Institution/University"
                          value={academic.institution}
                          onChange={(e) => updateAcademic(index, 'institution', e.target.value)}
                        />
                        <Input
                          type="number"
                          placeholder="Year of Completion"
                          value={academic.year}
                          onChange={(e) => updateAcademic(index, 'year', parseInt(e.target.value))}
                        />
                        <Input
                          placeholder="Specialization (optional)"
                          value={academic.specialization}
                          onChange={(e) => updateAcademic(index, 'specialization', e.target.value)}
                        />
                      </div>
                    </Card>
                  ))}
                </div>

                {/* Awards & Accolades */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-lg font-semibold flex items-center gap-2">
                      <Award className="w-5 h-5" /> Awards & Accolades
                    </Label>
                    <Button type="button" variant="outline" size="sm" onClick={addAccolade}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  
                  {formData.accolades.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No awards added. Click "Add" to add your achievements.
                    </p>
                  ) : (
                    formData.accolades.map((accolade, index) => (
                      <Card key={index} className="p-4 bg-muted/50">
                        <div className="flex justify-between items-start mb-3">
                          <span className="font-medium">Award {index + 1}</span>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeAccolade(index)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                          <Input
                            placeholder="Award Title"
                            value={accolade.title}
                            onChange={(e) => updateAccolade(index, 'title', e.target.value)}
                          />
                          <Input
                            placeholder="Organization/Festival"
                            value={accolade.organization}
                            onChange={(e) => updateAccolade(index, 'organization', e.target.value)}
                          />
                          <Input
                            type="number"
                            placeholder="Year"
                            value={accolade.year}
                            onChange={(e) => updateAccolade(index, 'year', parseInt(e.target.value))}
                          />
                          <Input
                            placeholder="Description (optional)"
                            value={accolade.description}
                            onChange={(e) => updateAccolade(index, 'description', e.target.value)}
                          />
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 4: Teaching Preferences */}
            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                {/* Subjects */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold">Subjects You Can Teach *</Label>
                  <p className="text-sm text-muted-foreground">Select all subjects you're qualified to teach</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {MENTOR_SUBJECTS.map((subject) => (
                      <div
                        key={subject}
                        onClick={() => toggleSubject(subject)}
                        className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          formData.subjects.includes(subject)
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-border hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={formData.subjects.includes(subject)} />
                          <span className="text-sm font-medium">{subject}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Teaching Experience */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Teaching Experience (Years)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.teaching_experience_years === 0 ? '' : formData.teaching_experience_years}
                      onChange={(e) => updateField('teaching_experience_years', e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                      placeholder="Enter years"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Availability (Hours/Week)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="40"
                      value={formData.availability_hours_per_week === 0 ? '' : formData.availability_hours_per_week}
                      onChange={(e) => updateField('availability_hours_per_week', e.target.value === '' ? 10 : parseInt(e.target.value) || 10)}
                      placeholder="Enter hours"
                    />
                  </div>
                </div>

                {/* Languages */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold">Preferred Teaching Languages</Label>
                  <div className="flex flex-wrap gap-2">
                    {LANGUAGES.map((lang) => (
                      <Badge
                        key={lang}
                        variant={formData.preferred_languages.includes(lang) ? 'default' : 'outline'}
                        className={`cursor-pointer px-4 py-2 ${
                          formData.preferred_languages.includes(lang)
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90'
                            : 'hover:bg-muted'
                        }`}
                        onClick={() => toggleLanguage(lang)}
                      >
                        {lang}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Bio */}
                <div className="space-y-2">
                  <Label htmlFor="bio">Bio / About Yourself</Label>
                  <Textarea
                    id="bio"
                    placeholder="Tell us about your teaching philosophy, experience, and what makes you unique as a mentor..."
                    value={formData.bio}
                    onChange={(e) => updateField('bio', e.target.value)}
                    rows={4}
                  />
                </div>
              </motion.div>
            )}

            {/* Step 5: Success */}
            {step === 5 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center">
                  <Check className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Registration Successful!</h2>
                <p className="text-muted-foreground mb-6">
                  Your mentor application has been submitted for review.
                </p>
                <Card className="max-w-md mx-auto p-6 bg-muted/50">
                  <p className="text-sm text-muted-foreground mb-2">Your Vendor ID</p>
                  <p className="text-2xl font-mono font-bold text-gradient-purple">
                    {/* This will be shown from the response */}
                    VIA-MENT-XXXXXX
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Keep this ID safe. You'll need it for tracking and payments.
                  </p>
                </Card>
                <div className="mt-8 flex justify-center gap-4">
                  <Button variant="outline" onClick={() => navigate('/')}>
                    Go Home
                  </Button>
                  <Button className="bg-gradient-to-r from-purple-500 to-pink-500 text-white" onClick={() => navigate('/login')}>
                    Login to Dashboard
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            {step < 5 && (
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={step === 1}
                  className="rounded-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                
                {step < 4 ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white"
                    data-testid="mentor-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <Check className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
